import {
  listLanguageVersions,
  type PlaygroundEngineId,
} from "../config/backend-language-contract.generated";

/**
 * The language version a lesson's Run link names (`LN-WEB02`).
 *
 * A "Run in Compiler" link (`a.pc-run`) is `/<locale>/playground/<lang>?code=…`
 * and carried no version, so a C++20 program from an "In C++20" block opened on
 * the Playground's default C++17 and stopped at its first C++20 line. The link
 * now says which version it was written for:
 *
 *     /en/playground/cpp?code=…&version=c%2B%2B20
 *
 * The value is a version **id** from the contract (`docs/24` §3.1), never a
 * flag or a toolchain, and it is only honoured when the contract offers it for
 * that language on the serving engine. Anything else (a missing parameter, an
 * old link, a typo, a version the language does not have) is ignored and the
 * Playground opens the way it always did. `course-content/tools/build-links.mjs`
 * writes the parameter for a block marked `data-std` or inside `.pc-cpp20`.
 */
export const RUN_LINK_VERSION_PARAM = "version";

/** The version id `raw` names for `languageId`, or null when the contract does not offer it. */
export function runLinkVersion(
  raw: string | null | undefined,
  languageId: string,
  engine: PlaygroundEngineId = "runner",
): string | null {
  if (!raw) return null;
  // A `+` written into a query string unencoded arrives as a space: "c  20"
  // can only have been "c++20", and no version id contains a space.
  const id = raw.trim().replace(/ /g, "+");
  const offered = listLanguageVersions(languageId, engine).some(
    (version) => version.id === id && version.enabled,
  );
  return offered ? id : null;
}

/** The `&version=…` tail for a Playground link, empty when there is no version to name. */
export function runLinkVersionQuery(version: string | null | undefined): string {
  return version ? `&${RUN_LINK_VERSION_PARAM}=${encodeURIComponent(version)}` : "";
}
